import React, { useRef, useState } from "react";
import axios from "axios";
import { API } from "@/auth";

/* Neo's voice (speak/listen) + phone notifications for the porch chat. Admin only. */

let current = null;

export async function speakNeo(text) {
  const r = await axios.post(`${API}/uncle-neo/speak`, { text });
  if (current) current.pause();
  const audio = new Audio(`data:${r.data.mime};base64,${r.data.audio_b64}`);
  audio.dataset.neo = "voice";
  current = audio;
  await audio.play();
  return audio;
}

export function stopNeoVoice() {
  if (current) current.pause();
}

// Whisper's usual guesses when it hears silence.
const NOISE = new Set(["", "you", "thank you", "thanks for watching", "bye", "okay", "uh", "um"]);

export function TalkButton({ onText, disabled }) {
  const [state, setState] = useState("idle");
  const rec = useRef(null);
  const chunks = useRef([]);
  const startedAt = useRef(0);

  const start = async (e) => {
    e.preventDefault();
    if (disabled || state !== "idle") return;
    stopNeoVoice();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const type = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const r = new MediaRecorder(stream, { mimeType: type });
      chunks.current = [];
      r.ondataavailable = (ev) => ev.data.size && chunks.current.push(ev.data);
      r.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (Date.now() - startedAt.current < 700) { setState("idle"); return; }
        setState("hearing");
        try {
          const fd = new FormData();
          fd.append("file", new Blob(chunks.current, { type }), type === "audio/webm" ? "talk.webm" : "talk.mp4");
          const res = await axios.post(`${API}/uncle-neo/listen`, fd);
          const heard = (res.data?.text || "").trim();
          if (heard.length > 1 && !NOISE.has(heard.toLowerCase().replace(/[^a-z ]/g, "").trim())) onText(heard);
        } catch { /* nothing heard */ }
        setState("idle");
      };
      rec.current = r;
      startedAt.current = Date.now();
      r.start();
      setState("recording");
    } catch {
      setState("idle");
    }
  };
  const stop = (e) => {
    e.preventDefault();
    if (rec.current && state === "recording") rec.current.stop();
  };

  const label = { idle: "hold to talk", recording: "listening… let go to send", hearing: "hearing you…" }[state];
  return (
    <button data-testid="uncle-neo-talk-btn" disabled={disabled || state === "hearing"}
            onMouseDown={start} onMouseUp={stop} onMouseLeave={stop} onTouchStart={start} onTouchEnd={stop}
            className={`w-full py-4 font-mono text-xs tracking-widest border transition-colors select-none ${
              state === "recording" ? "bg-[#f2b06b] text-black border-[#f2b06b]" : "bg-white/5 text-white/90 border-white/30 hover:bg-white/10"} disabled:opacity-40`}>
      {label}
    </button>
  );
}

function b64ToBytes(v) {
  const pad = "=".repeat((4 - (v.length % 4)) % 4);
  const raw = atob((v + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
}

export function PhoneLink() {
  const [msg, setMsg] = useState("");
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window;

  const enable = async () => {
    setMsg("setting up…");
    try {
      if (!supported) {
        setMsg("This browser can't get notifications. On iPhone: Share → Add to Home Screen, open it from there, then tap this again.");
        return;
      }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setMsg("Notifications were not allowed. Turn them on in your phone settings for this site."); return; }
      const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/public-key`);
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(data.publicKey) });
      await axios.post(`${API}/push/subscribe`, sub.toJSON());
      await axios.post(`${API}/push/test`);
      setMsg("Done. Neo can reach you on this phone now. A test buzz is on its way.");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Couldn't set it up on this device.");
    }
  };

  return (
    <div className="px-4 py-2 border-t border-white/10 flex flex-wrap items-center gap-3 text-[10px] font-mono tracking-widest text-white/60">
      <button data-testid="uncle-neo-phone-btn" onClick={enable}
              className="px-3 py-1.5 border border-white/30 hover:bg-white/10 text-white/90 transition-colors">
        let neo reach this phone
      </button>
      {msg && <span data-testid="uncle-neo-phone-msg" className="normal-case tracking-normal text-white/70">{msg}</span>}
    </div>
  );
}
